import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Breadcrumbs, Button, Chip, Collapse, InputAdornment, Link, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { MdiIcon, Table, WaitDialog } from '@components';
import { mdiFilterOff, mdiMagnify, mdiPlus, mdiTune, mdiUpload } from '@mdi/js';
import { useModal } from 'mui-modal-provider';
import ExportDialog, { Choice } from './export';
const DASH = '—';
// Referência estável: o Table reavalia os filtros sempre que o onFilter muda.
const noop = () => { };
const SEARCH_FIELDS = ['numeroProcesso', 'numeroParceiro', 'cui'];
// Uma só chave de filtro: o servidor procura o texto nos três campos com OR.
const SEARCH_KEY = SEARCH_FIELDS.join('|');
const MAIS_FILTROS = ['tipoGasId', 'tipoInstalacaoId', 'ordId', 'agendamentoNaHora'];
const OP_TO_TYPE = {
    $eq: 'equals',
    $ilike: 'contains',
    $sw: 'startsWith',
    $not: 'notEquals',
    $null: 'empty',
    $notNull: 'notEmpty',
    $gt: 'greaterThan',
    $gte: 'greaterThanOrEqualTo',
    $lt: 'lessThan',
    $lte: 'lessThanOrEqualTo',
    $btn: 'between'
};
// Cor da etiqueta por id de gas_estado_processo; os restantes ficam a cinzento.
const ESTADO_COR = { 2: 'info', 3: 'warning', 4: 'success', 8: 'secondary', 9: 'error', 10: 'primary' };
const Linhas = ({ a, b }) => (<Box sx={{ minWidth: 0 }}>
    <Typography variant='body2' noWrap>
      {a}
    </Typography>
    {b != undefined && (<Typography variant='caption' color='text.secondary' noWrap component='div'>
        {b}
      </Typography>)}
  </Box>);
const GasProcessosListView = ({ $state, GasService, UIService }) => {
    const loadParams = () => {
        const sortBy = $state.params.sortBy;
        if (sortBy == undefined) {
            return [];
        }
        const o = [];
        sortBy.forEach(r => {
            const s = r.split(':');
            o.push({ id: s[0], desc: s[1] == 'DESC' });
        });
        return o;
    };
    const { showModal } = useModal();
    const [data, setData] = useState();
    const [estados, setEstados] = useState([]);
    const [gas, setGas] = useState([]);
    const [instalacao, setInstalacao] = useState([]);
    const [inspecao, setInspecao] = useState([]);
    const [ord, setOrd] = useState([]);
    const [distribuidor, setDistribuidor] = useState([]);
    const [tecnicos, setTecnicos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [options, setOptions] = useState({
        filter: [],
        sort: []
    });
    const [pagination, setPagination] = useState({
        pageIndex: $state.params.page - 1 || 0,
        pageSize: $state.params.limit || 20
    });
    // Filtros gravados no URL ({id: "$op:valor"}), no formato que o fetchData escreve.
    const loadFilter = () => {
        try {
            const raw = $state.params.filter;
            const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (!obj || typeof obj !== 'object')
                return [];
            return Object.entries(obj).map(([id, v]) => {
                const m = String(v).match(/^(\$[^:]+):([\s\S]*)$/);
                return m ? { id, type: OP_TO_TYPE[m[1]] ?? 'equals', value: m[2] } : { id, type: 'equals', value: String(v) };
            });
        }
        catch {
            return [];
        }
    };
    const [filter, setFilter] = useState(() => loadFilter());
    const [sort, setSort] = useState(() => loadParams());
    const [totalTodos, setTotalTodos] = useState();
    const [maisFiltros, setMaisFiltros] = useState(() => filter.some(f => MAIS_FILTROS.includes(f.id)));
    // Lê também os filtros de um só campo de URLs guardados antes da pesquisa única.
    const [searchText, setSearchText] = useState(() => filter.find(f => f.id === SEARCH_KEY || SEARCH_FIELDS.includes(f.id))?.value ?? '');
    const filterRef = useRef(filter);
    filterRef.current = filter;
    const lastReq = useRef(0);
    const getF = (id) => filter.find(f => f.id === id);
    // Aplica várias alterações de uma vez (null remove o filtro) e volta à primeira página.
    const changeFilters = (changes) => {
        const prev = filterRef.current;
        const next = prev.filter(f => !(f.id in changes));
        Object.entries(changes).forEach(([id, c]) => {
            if (c && c[1] !== '')
                next.push({ id, type: c[0], value: c[1] });
        });
        const key = (l) => JSON.stringify([...l].sort((a, b) => a.id.localeCompare(b.id)));
        if (key(next) === key(prev))
            return;
        filterRef.current = next;
        setFilter(next);
        setPagination(p => ({ ...p, pageIndex: 0 }));
    };
    const limparFiltros = () => {
        setSearchText('');
        filterRef.current = [];
        setFilter([]);
        setPagination(p => ({ ...p, pageIndex: 0 }));
    };
    const rangeOf = (id) => {
        const f = getF(id);
        if (!f)
            return ['', ''];
        const [de, ate] = f.value.split(';');
        if (f.type === 'between')
            return [de ?? '', ate ?? ''];
        if (f.type === 'greaterThanOrEqualTo')
            return [f.value, ''];
        if (f.type === 'lessThanOrEqualTo')
            return ['', f.value];
        return [f.value, f.value];
    };
    const setRange = (id, de, ate) => changeFilters({ [id]: de && ate ? ['between', `${de};${ate}`] : de ? ['greaterThanOrEqualTo', de] : ate ? ['lessThanOrEqualTo', ate] : null });
    useEffect(() => {
        const t = setTimeout(() => {
            const v = searchText.trim();
            changeFilters({ ...Object.fromEntries(SEARCH_FIELDS.map(id => [id, null])), [SEARCH_KEY]: v ? ['contains', v] : null });
        }, 400);
        return () => clearTimeout(t);
    }, [searchText]);
    const importProcessos = () => {
        UIService.showDialog({
            template: require('./import/dialog.html'),
            controller: 'GasImporterController',
            controllerAs: 'vm'
        })
            .then(r => {
            UIService.addToast('Ficheiro importado com sucesso');
            fetchData();
        })
            .catch(e => {
            if (e === 'error') {
                UIService.addToast('Não foi possível importar ficheiro');
                fetchData();
            }
            else {
                fetchData();
            }
        });
    };
    // Colunas do Excel exportado: não mexer (ND-2 do CR-SLD20180014-0008).
    const exportColumns = useMemo(() => [
        {
            accessorKey: 'id',
            header: '#',
            size: 100,
            Cell: ({ renderedCellValue, row }) => (<Link underline='none' color='default' sx={{ fontWeight: 'bold' }} target={'_blank'} href={$state.href('app.gas.processos.details', { id: row.original.id }, { absolute: true })}>
            {renderedCellValue}
          </Link>)
        },
        {
            accessorKey: 'numeroProcesso',
            header: 'Nº Processo',
            size: 100,
            Cell: ({ renderedCellValue, row }) => (<Link underline='none' color='default' sx={{ fontWeight: 'bold' }} target={'_blank'} href={$state.href('app.gas.processos.details', { id: row.original.id }, { absolute: true })}>
            {renderedCellValue}
          </Link>)
        },
        {
            accessorKey: 'numeroParceiro',
            header: 'Nº Parceiro',
            size: 100
        },
        {
            accessorKey: 'agendamento.data',
            header: 'Data de Inspeção',
            size: 150,
            Cell: ({ renderedCellValue, row }) => (row.original.agendamento && row.original.agendamento.data && moment(row.original.agendamento.data).isValid() ? moment(row.original.agendamento.data).format('DD/MM/YYYY') : 'N/D')
        },
        {
            accessorKey: 'agendamento.hora',
            header: 'Hora',
            size: 50,
            Cell: ({ renderedCellValue, row }) => (row.original.agendamento && row.original.agendamento.hora ? row.original.agendamento.hora.substring(0, 5) : 'N/D')
        },
        {
            accessorKey: 'agendamento.duracao',
            header: 'Duração (min)',
            size: 80
        },
        {
            accessorKey: 'agendamento.tecnicoId',
            exportKey: 'agendamento.tecnico.name',
            header: 'Técnico',
            filterSelectOptions: tecnicos.map(r => {
                return {
                    label: r.name,
                    value: r.id
                };
            }),
            size: 140,
            Cell: ({ row }) => row.original?.agendamento?.tecnico?.name
        },
        {
            accessorKey: 'estadoId',
            exportKey: 'estado.designacao',
            header: 'Estado',
            filterSelectOptions: estados.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            size: 140,
            Cell: ({ row }) => row.original?.estado?.designacao
        },
        {
            accessorKey: 'cui',
            header: 'CUI',
            size: 200
        },
        {
            accessorKey: 'tipoGasId',
            exportKey: 'tipoGas.designacao',
            header: 'Tipo de Gás',
            filterSelectOptions: gas.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            size: 140,
            Cell: ({ row }) => row.original?.tipoGas?.designacao
        },
        {
            accessorKey: 'tipoInstalacaoId',
            exportKey: 'tipoInstalacao.designacao',
            header: 'Tipo de Instalação',
            filterSelectOptions: instalacao.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            size: 140,
            Cell: ({ row }) => row.original?.tipoInstalacao?.designacao
        },
        {
            accessorKey: 'tipoInspecaoId',
            exportKey: 'tipoInspecao.designacao',
            header: 'Tipo de Inspeção',
            filterSelectOptions: inspecao.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            size: 140,
            Cell: ({ renderedCellValue, row }) => row.original?.tipoInspecao?.designacao
        },
        {
            accessorKey: 'distribuidorId',
            exportKey: 'distribuidor.designacao',
            header: 'Distribuidor',
            filterSelectOptions: distribuidor.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            size: 150,
            Cell: ({ renderedCellValue, row }) => row.original?.distribuidor?.designacao
        },
        {
            accessorKey: 'ordId',
            exportKey: 'ord.designacao',
            header: 'ORD',
            filterSelectOptions: ord.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            size: 150,
            Cell: ({ renderedCellValue, row }) => row.original?.ord?.designacao
        },
        {
            accessorKey: 'agendamentoNaHora',
            header: 'Agendamento na Hora',
            size: 75,
            Cell: ({ renderedCellValue, row }) => (row.original.agendamentoNaHora ? 'Sim' : 'Não')
        },
        {
            accessorKey: 'dataImportacao',
            header: 'Data de Importação',
            size: 150,
            Cell: ({ renderedCellValue, row }) => (moment(row.original.dataImportacao).isValid() ? moment(row.original.dataImportacao).format('DD/MM/YYYY') : 'N/D')
        }
    ], [gas, estados, instalacao, inspecao, ord, distribuidor, tecnicos]);
    // Colunas da vista: agrupadas, com as mesmas accessorKey para manter a ordenação no servidor.
    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: '#',
            size: 90,
            visibility: false
        },
        {
            accessorKey: 'numeroProcesso',
            header: 'Processo',
            size: 150,
            Cell: ({ row }) => (<Linhas a={<Link underline='none' color='default' sx={{ fontWeight: 'bold' }} target={'_blank'} href={$state.href('app.gas.processos.details', { id: row.original.id }, { absolute: true })}>
                {row.original.numeroProcesso || DASH}
              </Link>} b={row.original.numeroParceiro ? `Parceiro ${row.original.numeroParceiro}` : undefined}/>)
        },
        {
            accessorKey: 'agendamento.data',
            header: 'Inspeção',
            size: 160,
            Cell: ({ row }) => {
                const ag = row.original.agendamento;
                if (!ag?.data || !moment(ag.data).isValid()) {
                    return (<Typography variant='body2' color='text.secondary' fontStyle='italic'>
                Sem inspeção marcada
              </Typography>);
                }
                return <Linhas a={moment(ag.data).format('DD/MM/YYYY')} b={`${ag.hora ? ag.hora.substring(0, 5) : DASH} · ${ag.duracao ?? DASH} min`}/>;
            }
        },
        {
            accessorKey: 'agendamento.tecnicoId',
            header: 'Técnico',
            size: 150,
            Cell: ({ row }) => <Linhas a={row.original?.agendamento?.tecnico?.name || DASH}/>
        },
        {
            accessorKey: 'estadoId',
            header: 'Estado',
            size: 210,
            Cell: ({ row }) => (row.original?.estado ? <Chip size='small' color={ESTADO_COR[row.original.estadoId] ?? 'default'} label={row.original.estado.designacao}/> : DASH)
        },
        {
            accessorKey: 'tipoInstalacaoId',
            header: 'Instalação',
            size: 180,
            Cell: ({ row }) => <Linhas a={[row.original?.tipoGas?.designacao, row.original?.tipoInstalacao?.designacao].filter(Boolean).join(' · ') || DASH} b={row.original?.tipoInspecao?.designacao || DASH}/>
        },
        {
            accessorKey: 'cui',
            header: 'CUI',
            size: 200,
            Cell: ({ row }) => <Linhas a={row.original.cui || DASH}/>
        },
        {
            accessorKey: 'distribuidorId',
            header: 'Distribuidor · ORD',
            size: 160,
            Cell: ({ row }) => <Linhas a={row.original?.distribuidor?.designacao || DASH} b={row.original?.ord?.designacao || DASH}/>
        },
        {
            accessorKey: 'dataImportacao',
            header: 'Importado',
            size: 110,
            Cell: ({ row }) => (moment(row.original.dataImportacao).isValid() ? moment(row.original.dataImportacao).format('DD/MM/YYYY') : DASH)
        },
        {
            accessorKey: 'agendamentoNaHora',
            header: 'Agendamento na Hora',
            size: 90,
            visibility: false,
            Cell: ({ row }) => (row.original.agendamentoNaHora ? 'Sim' : 'Não')
        }
    ], []);
    // O Table recebe só a ordenação: sem filtros declarados, desliga os filtros por coluna (ficam na barra).
    const tableOptions = useMemo(() => ({ filter: [], sort: options.sort }), [options]);
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            case 'between':
                return '$btn';
            default:
                return '$eq';
        }
    };
    // Pedidos independentes, em paralelo: cada lista aparece logo que chega.
    const initialFetch = () => {
        GasService.optionsProcessos().then(setOptions);
        GasService.getTecnicosFuncionario().then(setTecnicos);
        GasService.getEstados().then(setEstados);
        GasService.getTiposGas().then(setGas);
        GasService.getTiposInstalacao().then(setInstalacao);
        GasService.getTiposInspecao().then(setInspecao);
        GasService.getOrds().then(setOrd);
        GasService.getDistribuidores().then(setDistribuidor);
    };
    const fetchData = async () => {
        const req = ++lastReq.current;
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const aux = await GasService.findProcessos({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            // Uma resposta antiga que chegue depois da mais recente não pode substituir a lista.
            if (req !== lastReq.current)
                return;
            $state.go($state.current.name, {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            // Update URL
            console.log(aux);
            setData(aux);
        }
        catch (e) { }
        if (req === lastReq.current)
            setRefetching(false);
    };
    const excelGen = async (id, array) => {
        const wait = showModal(WaitDialog, { title: 'Por favor aguarde...' });
        let filename = `gas-processos-${id ? 'selecao' : 'exportacao'}-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
        let filters = {};
        filter.forEach(r => {
            filters[r.id] = `${getType(r.type)}:${r.value}`;
        });
        if (id) {
            filters['id'] = `$in:${array.join(',')}`;
        }
        else {
            // Aplica filtro temporal
        }
        let sorter = [];
        sort.forEach(r => {
            sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
        });
        try {
            let o = await GasService.exportProcessos({
                columns: exportColumns.map(r => {
                    return {
                        accessorKey: r.exportKey ?? r.accessorKey,
                        header: r.header
                    };
                }),
                filter: filters,
                sortBy: sorter
            });
            if (o) {
                // Decode Base64 and convert to a Blob
                const binary = atob(o.$data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                // Create a Blob and save it as an Excel file
                const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                link.click();
            }
        }
        catch (e) {
            if (e && e.data) {
                UIService.addToast(e.data.message);
            }
            else {
                UIService.addToast('Ocorreu um erro ao exportar dados!');
            }
        }
        wait.destroy();
    };
    const primaveraGen = async () => {
        const wait = showModal(WaitDialog, { title: 'Por favor aguarde...' });
        let filename = `export-faturacao-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
        let filters = {};
        filter.forEach(r => {
            filters[r.id] = `${getType(r.type)}:${r.value}`;
        });
        let sorter = [];
        sort.forEach(r => {
            sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
        });
        try {
            let o = await GasService.exportProcessosFatura({
                filter: filters,
                sortBy: sorter
            });
            if (o) {
                // Decode Base64 and convert to a Blob
                const binary = atob(o.$data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                // Create a Blob and save it as an Excel file
                const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                link.click();
            }
        }
        catch (e) {
            if (e && e.data) {
                UIService.addToast(e.data.message);
            }
            else {
                UIService.addToast('Ocorreu um erro ao exportar dados!');
            }
        }
        wait.destroy();
    };
    useEffect(() => {
        initialFetch();
    }, []);
    useEffect(() => {
        setRefetching(true);
        fetchData();
    }, [pagination, filter, sort]);
    const estadoF = getF('estadoId');
    useEffect(() => {
        if (data && !estadoF)
            setTotalTodos(data.meta.totalItems);
    }, [data]);
    const tabValue = !estadoF ? 'todos' : estadoF.type === 'equals' && estados.some(e => String(e.id) === estadoF.value) ? estadoF.value : false;
    const opcoes = (l) => l.map(r => ({ value: String(r.id), label: r.designacao ?? r.name }));
    const filtroSelect = (label, id, opts) => {
        const v = getF(id)?.value ?? '';
        return (<TextField select size='small' label={label} value={opts.some(o => o.value === v) ? v : ''} onChange={e => changeFilters({ [id]: e.target.value === '' ? null : ['equals', String(e.target.value)] })} sx={{ minWidth: 170 }}>
        <MenuItem value=''>Todos</MenuItem>
        {opts.map(o => (<MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>))}
      </TextField>);
    };
    const [inspDe, inspAte] = rangeOf('agendamento.data');
    return (<Box>
      <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant='h5'>Processos</Typography>
          <Breadcrumbs aria-label='breadcrumb'>
            <Link underline='none' color='inherit'>
              Gás
            </Link>
            <Link underline='hover' color='text.primary' aria-current='page'>
              Processos
            </Link>
          </Breadcrumbs>
        </Box>
        <Stack direction='row' gap={1.5}>
          <Button variant='outlined' startIcon={<MdiIcon path={mdiUpload}/>} onClick={() => importProcessos()}>
            Importar Excel EDP
          </Button>
          <Button variant='contained' color='success' startIcon={<MdiIcon path={mdiPlus}/>} onClick={() => $state.go('app.gas.processos.add')}>
            Novo processo
          </Button>
        </Stack>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => changeFilters({ estadoId: v === 'todos' ? null : ['equals', String(v)] })} variant='scrollable' scrollButtons='auto' aria-label='Filtrar por estado' sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab value='todos' label={totalTodos != undefined ? `Todos (${totalTodos})` : 'Todos'}/>
        {estados.map(e => (<Tab key={e.id} value={String(e.id)} label={e.designacao}/>))}
      </Tabs>

      <Stack direction='row' gap={1.5} alignItems='center' flexWrap='wrap' sx={{ py: 2 }}>
        <TextField size='small' label='Procurar processo' placeholder='Nº do processo, nº do parceiro ou CUI' value={searchText} onChange={e => setSearchText(e.target.value)} sx={{ flex: '1 1 340px', minWidth: 200, maxWidth: 340 }} InputProps={{
            startAdornment: (<InputAdornment position='start'>
                <MdiIcon path={mdiMagnify}/>
              </InputAdornment>)
        }}/>
        {filtroSelect('Técnico', 'agendamento.tecnicoId', opcoes(tecnicos))}
        {filtroSelect('Distribuidor', 'distribuidorId', opcoes(distribuidor))}
        <TextField type='date' size='small' label='Inspeção de' InputLabelProps={{ shrink: true }} value={inspDe.substring(0, 10)} onChange={e => setRange('agendamento.data', e.target.value, inspAte)}/>
        <TextField type='date' size='small' label='Inspeção até' InputLabelProps={{ shrink: true }} value={inspAte.substring(0, 10)} onChange={e => setRange('agendamento.data', inspDe, e.target.value)}/>
        {filtroSelect('Tipo de inspeção', 'tipoInspecaoId', opcoes(inspecao))}
        <Button startIcon={<MdiIcon path={mdiTune}/>} onClick={() => setMaisFiltros(!maisFiltros)} aria-expanded={maisFiltros}>
          {maisFiltros ? 'Menos filtros' : 'Mais filtros'}
        </Button>
        {filter.length > 0 && (<Button color='inherit' startIcon={<MdiIcon path={mdiFilterOff}/>} onClick={limparFiltros}>
            Limpar filtros
          </Button>)}
      </Stack>
      <Collapse in={maisFiltros}>
        <Stack direction='row' gap={1.5} alignItems='center' flexWrap='wrap' sx={{ pb: 2 }}>
          {filtroSelect('Tipo de gás', 'tipoGasId', opcoes(gas))}
          {filtroSelect('Tipo de instalação', 'tipoInstalacaoId', opcoes(instalacao))}
          {filtroSelect('ORD', 'ordId', opcoes(ord))}
          {filtroSelect('Agendamento na hora', 'agendamentoNaHora', [
            { value: '1', label: 'Sim' },
            { value: '0', label: 'Não' }
        ])}
        </Stack>
      </Collapse>

      <Box>
        <Table onDoubleClick={row => {
            window.open($state.href('app.gas.processos.details', { id: row.id }, { absolute: true }));
        }} storageKey={`${$state.current.name}.v2`} data={data} columns={columns} initial={{
            sort: sort,
            filter: []
        }} isLoading={refetching} options={tableOptions} onExport={async () => {
            showModal(ExportDialog, {
                maxWidth: 'sm',
                onClose: async (choice) => {
                    console.log(choice);
                    if (choice == Choice.PROCESSO) {
                        excelGen(false, []);
                    }
                    if (choice == Choice.FATURA) {
                        primaveraGen();
                    }
                }
            });
            //
        }} selectActions={selected => {
            return [
                <Button onClick={async () => {
                        if (selected.length > 0) {
                            excelGen(true, selected);
                        }
                    }}>
                Exportar
              </Button>
            ];
        }} selectable={row => {
            return true;
        }} onPaginate={setPagination} onFilter={noop} onSort={setSort}/>
      </Box>
    </Box>);
};
export default GasProcessosListView;
